import {Assert} from "js-vextensions";
import {GetOpenMapID} from "../../Store/main/maps.js";
import {GetMapState} from "../../Store/main/maps/$mapState.js";
import {Rect, Vec2, Vec3} from "./VectorStructs.js";
import * as THREE from "three";

export function GetWorldPosFromClientPos(clientPos: Vec2, canvasClientRect: DOMRect, cameraPos: Vec3, cameraLRTBZ: {left: number, right: number, top: number, bottom: number, zoom: number}) {
	// Get canvas-relative mouse coordinates
	const mouseX = clientPos.x - canvasClientRect.left;
	const mouseY = clientPos.y - canvasClientRect.top;

	// Convert to normalized device coordinates (0 - 1)
	const normalizedX = (mouseX / canvasClientRect.width).Distance(0);
	const normalizedY = (mouseY / canvasClientRect.height).Distance(1);

	const camViewWidth = (cameraLRTBZ.right - cameraLRTBZ.left) / cameraLRTBZ.zoom;
	const camViewHeight = (cameraLRTBZ.top - cameraLRTBZ.bottom) / cameraLRTBZ.zoom;

	// For orthographic camera, we can directly map to world space
	// based on the camera's current properties
	const worldX = cameraPos.x - (camViewWidth / 2) + (normalizedX * camViewWidth);
	const worldY = cameraPos.y - (camViewHeight / 2) + (normalizedY * camViewHeight);

	// Z position depends on what plane you're interested in
	// If you're working with a ground plane at z=0, just use 0
	const worldZ = 0;

	Assert(worldX != Infinity && worldY != Infinity && !isNaN(worldX) && !isNaN(worldY), [
		`Has NaN or Infinity in worldPos calc!`,
		`@worldX:${worldX} @worldY:${worldY}`,
		`@canvasClientRect:${JSON.stringify(canvasClientRect)}`,
		`@clientPos:${JSON.stringify(clientPos)}`,
		`@cameraPos:${JSON.stringify(cameraPos)}`,
		`@cameraLRTBZ:${JSON.stringify(cameraLRTBZ)}`
	].join("\n"));

	return new Vec3(worldX, worldY, worldZ);
}
export function GetClientPosFromWorldPos(worldPos: Vec2, canvasClientRect: DOMRect, cameraPos: Vec3, cameraLRTBZ: {left: number, right: number, top: number, bottom: number, zoom: number}) {
	const camViewWidth = (cameraLRTBZ.right - cameraLRTBZ.left) / cameraLRTBZ.zoom;
	const camViewHeight = (cameraLRTBZ.top - cameraLRTBZ.bottom) / cameraLRTBZ.zoom;
	const normalizedX = (worldPos.x - (cameraPos.x - (camViewWidth / 2))) / camViewWidth;
	const normalizedY = (worldPos.y - (cameraPos.y - (camViewHeight / 2))) / camViewHeight;
	const mouseX = canvasClientRect.left + (normalizedX * canvasClientRect.width);
	const mouseY = canvasClientRect.bottom - (normalizedY * canvasClientRect.height);
	return new Vec2(mouseX, mouseY);
}

export function MaybeSnapWorldPos(worldPos: Vec2) {
	const mapState = GetMapState.CatchBail(null, GetOpenMapID.CatchBail(null));
	if (!mapState || !mapState.snap_pos_enabled) return worldPos;
	const snapSize = mapState.snap_pos_step;
	if (snapSize == 0) return worldPos; // no snapping, if snapSize is 0/disabled
	return new Vec2(worldPos.x.RoundTo(snapSize), worldPos.y.RoundTo(snapSize));
}
export function MaybeSnapPosChange(posChange: Vec2) {
	const mapState = GetMapState.CatchBail(null, GetOpenMapID.CatchBail(null));
	if (!mapState || !mapState.snap_pos_enabled) return posChange;
	const snapSize = mapState.snap_pos_step;
	if (snapSize == 0) return posChange; // no snapping, if snapSize is 0/disabled
	return new Vec2(posChange.x.RoundTo(snapSize), posChange.y.RoundTo(snapSize));
}

export function GetAngleInDegreesOfLine(start: Vec2, end: Vec2) {
	const direction = end.Minus(start);
	return GetAngleInDegreesOfDirection(direction);
}
export function GetAngleInDegreesOfDirection(direction: Vec2) {
	//const direction = end.Minus(start);
	const angle_radians = Math.atan2(-direction.y, direction.x); // negate y to get clockwise rotation
	const angle_radians_with0Up = angle_radians + (Math.PI / 2); // rotate coordinate system, so 0-degrees is up
	const angle_with0Up = angle_radians_with0Up * (180 / Math.PI); // convert to degrees
	const angle_with0Up_inRange = angle_with0Up.WrapToRange(-180, 180); // wrap to range of: [-180, 180)
	return angle_with0Up_inRange;
}

/** Rotates a 2D offset vector by the given rotation (degrees, clockwise). */
export function RotateOffsetByRotation(offset: Vec2, rotationDeg: number) {
	// negate rotation, to convert from "rotation is clockwise" meaning in this app, to standard counter-clockwise rotation (which code below assumes)
	const rotationRadians = -((rotationDeg * Math.PI) / 180);
	const cos = Math.cos(rotationRadians);
	const sin = Math.sin(rotationRadians);
	return new Vec2(
		offset.x * cos - offset.y * sin,
		offset.x * sin + offset.y * cos
	);
}
// note: the two funcs below are just "more specific" proxies for `RotateOffsetByRotation`

/** For some target, at local-point `localPoint` within object `obj`, returns the world-offset of target from `obj`'s origin/world-pos.
(this returns a world OFFSET; if you need the final world POSITION, convert the world-offset into a world-position using `ResolveObjPosInWorld(obj).Plus(worldOffset)`) */
export function TransformObjLocalPointToWorldOffset_UsingRotationOnly(localPoint: Vec2, objTotalRotation: number) {
	return RotateOffsetByRotation(localPoint, objTotalRotation);
}
/** Translate a given world-offset into a position-change within the local-space of `obj`.
(this receives a world OFFSET; if you need to convert a world POSITION into local-space, first convert that world-position into a world-offset using `worldPos.Minus(ResolveObjPosInWorld(obj))`) */
export function TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffset: Vec2, objTotalRotation: number) {
	return RotateOffsetByRotation(worldOffset, -objTotalRotation);
}

// commented atm, since not yet tested/fully-logic-confirmed
/*export function TransformObjLocalPointToWorldPosition(localPoint: Vec2, objRot: number, objWorldPos: Vec3) {
	const worldOffset = TransformObjLocalPointToWorldOffset_UsingRotationOnly(localPoint, objRot);
	return objWorldPos.Plus(worldOffset.ToVec3(0));
}
export function TransformWorldPositionToObjLocalPoint(worldPos: Vec3, objRot: number, objWorldPos: Vec3) {
	const worldOffset = worldPos.Minus(objWorldPos).ToVec2();
	return TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffset, objRot);
}*/

// Helper function to check if a rectangle intersects with an ellipse
export function DoesRectangleIntersectEllipse(rect: Rect, ellipseCenterX: number, ellipseCenterY: number, ellipseRadiusX: number, ellipseRadiusY: number): boolean {
	// 1. Find closest point on rectangle to ellipse center
	const closestX = Math.max(rect.x, Math.min(ellipseCenterX, rect.Right));
	const closestY = Math.max(rect.y, Math.min(ellipseCenterY, rect.Top));

	// 2. Calculate distance from closest point to ellipse center
	const dx = closestX - ellipseCenterX;
	const dy = closestY - ellipseCenterY;

	// 3. Check if the distance is within the ellipse radius (normalized)
	const distanceSquared = (dx * dx) / (ellipseRadiusX * ellipseRadiusX) +
							(dy * dy) / (ellipseRadiusY * ellipseRadiusY);

	// If the closest point is inside or on the boundary
	if (distanceSquared <= 1) {
		return true;
	}

	// 4. Additionally, check if any rectangle corner is inside the ellipse
	// This handles cases where the ellipse is entirely inside the rect
	const corners = [
		{x: rect.x, y: rect.y},
		{x: rect.Right, y: rect.y},
		{x: rect.x, y: rect.Top},
		{x: rect.Right, y: rect.Top}
	];

	for (const corner of corners) {
		const normalizedX = (corner.x - ellipseCenterX) / ellipseRadiusX;
		const normalizedY = (corner.y - ellipseCenterY) / ellipseRadiusY;
		if (normalizedX * normalizedX + normalizedY * normalizedY <= 1) {
			return true;
		}
	}

	return false;
}